import { z } from 'zod';
/**
 * Staff calendar (WS-2) — shared contracts. A pragmatic internal calendar for a
 * small team: staff create events (meeting / Besichtigung / admin-task), invite
 * other staff and accept/decline. A `besichtigung` targets a Makler as attendee
 * so it lands on their agenda. MS-Graph stays a WRITE-ONLY stub seam.
 *
 * Enums live here as the single source of truth so the CRM dropdowns and the
 * backend validation use the identical option lists (mirrors the Prisma enums
 * `CalendarEventType` / `CalendarAttendeeStatus`).
 */
export const CALENDAR_EVENT_TYPES = ['meeting', 'besichtigung', 'admin_task', 'other'];
export const calendarEventTypeSchema = z.enum(CALENDAR_EVENT_TYPES);
export const CALENDAR_ATTENDEE_STATUSES = ['invited', 'accepted', 'declined'];
export const calendarAttendeeStatusSchema = z.enum(CALENDAR_ATTENDEE_STATUSES);
/** Attendees answer only their OWN invitation → accepted | declined. */
export const calendarRespondStatusSchema = z.enum(['accepted', 'declined']);
const titleSchema = z.string().trim().min(1).max(200);
const locationSchema = z.string().trim().max(300);
const notesSchema = z.string().trim().max(5000);
/** Bounded — a 3-person shop never invites 50 staff, but a cap guards the query. */
const attendeeIdsSchema = z.array(z.string().uuid()).max(50);
/**
 * List query (GET /calendar/events). `from`/`to` bound the window; an event is
 * returned when it OVERLAPS [from,to]. `mine=true` → only events the caller
 * created or is invited to. Booleans arrive as query strings, so `mine` is
 * matched strictly against 'true' (z.coerce.boolean would treat 'false' as true).
 */
export const calendarEventsQuerySchema = z.object({
    from: z.coerce.date(),
    to: z.coerce.date(),
    type: calendarEventTypeSchema.optional(),
    mine: z.preprocess((v) => v === 'true' || v === true, z.boolean()).optional(),
});
/** Create a calendar event (POST /calendar/events). */
export const createCalendarEventSchema = z
    .object({
    type: calendarEventTypeSchema.default('meeting'),
    title: titleSchema,
    startAt: z.string().datetime(),
    endAt: z.string().datetime(),
    location: locationSchema.optional(),
    notes: notesSchema.optional(),
    attendeeUserIds: attendeeIdsSchema.optional(),
    mandateId: z.string().uuid().optional(),
})
    .refine((v) => new Date(v.endAt) > new Date(v.startAt), {
    message: 'end_before_start',
    path: ['endAt'],
});
/**
 * Partial update (PATCH /calendar/events/:id). `attendeeUserIds` REPLACES the
 * attendee set when present. The both-present time order is enforced here; the
 * service does a final start<end check after merging with the stored values.
 */
export const updateCalendarEventSchema = z
    .object({
    type: calendarEventTypeSchema,
    title: titleSchema,
    startAt: z.string().datetime(),
    endAt: z.string().datetime(),
    location: locationSchema.nullable(),
    notes: notesSchema.nullable(),
    attendeeUserIds: attendeeIdsSchema,
})
    .partial()
    .refine((v) => Object.keys(v).length > 0, { message: 'empty_update' })
    .refine((v) => !(v.startAt && v.endAt) || new Date(v.endAt) > new Date(v.startAt), {
    message: 'end_before_start',
    path: ['endAt'],
});
/** Respond to an invitation (POST /calendar/events/:id/respond). */
export const calendarRespondSchema = z.object({ status: calendarRespondStatusSchema });
